import { LoadingStatuses } from '@/common/constants';
import { DialogType } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import classNames from 'classnames';
import moment from 'moment';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import React, { createContext, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledDialog = styled(Dialog)<{ type?: DialogType }>`
  width: ${({ type }) => (type === DialogType.WARNING ? '550px' : '450px')};

  &.dialog-warning {
    .p-dialog-content {
      padding: var(--default-padding) var(--big-padding);
    }
    .p-dialog-footer {
      padding: var(--big-padding);
      padding-top: 0;
      label {
        font-size: var(--small-font-size) !important;
      }
    }
  }
`;

type DialogState = {
  type: DialogType | undefined;
  show: boolean;
  message?: string;
  customContent?: ReactNode;
  showDialogConfirmation?: boolean;
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
  destructive?: boolean;
  header: string;
  onAccept?: () => void;
  onReject?: () => void;
  closeImmediatelyAfterAccept?: boolean;
  confirmationMessage?: string;
  confirmationConst?: string;
  showUpload?: (
    resetDialogData: () => void,
    onDoNotShowAgain: () => void,
  ) => ReactNode;
};

export const DialogContext = createContext<{
  setDialogData: React.Dispatch<React.SetStateAction<DialogState>>;
}>({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setDialogData: () => {},
});

export const ConfirmationDialog: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { t } = useTranslation();
  const [confirmation, setConfirmation] = useState<boolean>(false);
  const [skip, setSkip] = useState<boolean>(false);
  const [isAccepting, setIsAccepting] = useState<boolean>(false);

  const [dialogData, setDialogData] = useState<DialogState>({
    type: undefined,
    show: false,
    header: '',
    message: '',
    customContent: undefined,
    showDialogConfirmation: true,
    confirmButtonLabel: t('generic.continue'),
    cancelButtonLabel: undefined,
    destructive: false,
    onAccept: undefined,
    onReject: undefined,
    closeImmediatelyAfterAccept: false,
    confirmationMessage: '',
    confirmationConst: undefined,
    showUpload: undefined,
  });

  const resetDialogData = () => {
    setSkip(false);
    setIsAccepting(false);
    setDialogData({
      show: false,
      header: '',
      message: '',
      customContent: undefined,
      showDialogConfirmation: true,
      confirmButtonLabel: t('generic.continue'),
      cancelButtonLabel: undefined,
      destructive: false,
      onAccept: undefined,
      type: undefined,
      onReject: undefined,
      closeImmediatelyAfterAccept: false,
      confirmationMessage: '',
      confirmationConst: undefined,
      showUpload: undefined,
    });
  };

  const onDoNotShowAgain = () => {
    if (!dialogData.confirmationConst) return;
    if (skip) {
      localStorage.setItem(
        dialogData.confirmationConst,
        moment().toISOString(),
      );
    } else {
      localStorage.removeItem(dialogData.confirmationConst);
    }
  };

  const infoFooter = (
    <AppButton
      label={t('dialog.ok')}
      icon="pi pi-check"
      severity="secondary"
      onClick={resetDialogData}
    />
  );

  const handleAccept = async (beforeAccept?: () => void) => {
    if (isAccepting) return;

    beforeAccept?.();

    if (dialogData.closeImmediatelyAfterAccept) {
      resetDialogData();
    }

    if (dialogData.onAccept) {
      setIsAccepting(true);
      try {
        await dialogData.onAccept();
      } finally {
        setIsAccepting(false);
      }
    }

    resetDialogData();
  };

  const acceptState = isAccepting
    ? LoadingStatuses.LOADING
    : LoadingStatuses.IDLE;

  const confirmationFooter = (
    <>
      <AppButton
        label={dialogData.confirmButtonLabel || t('dialog.yes')}
        icon="pi pi-check"
        severity="secondary"
        type="text"
        state={acceptState}
        onClick={() => handleAccept()}
        isDisabled={
          isAccepting ||
          (dialogData.confirmationMessage ? !confirmation : false)
        }
      />
      <AppButton
        label={dialogData.cancelButtonLabel || t('dialog.no')}
        severity="secondary"
        icon="pi pi-times"
        isDisabled={isAccepting}
        onClick={() => {
          if (dialogData.onReject) dialogData.onReject();
          resetDialogData();
        }}
      />
    </>
  );

  const destructiveConfirmationFooter = (
    <FlexContainer justify="flex-start" gap={16}>
      <AppButton
        label={dialogData.confirmButtonLabel || t('dialog.yes')}
        severity="danger"
        state={acceptState}
        onClick={() => handleAccept()}
        isDisabled={
          isAccepting ||
          (dialogData.confirmationMessage ? !confirmation : false)
        }
      />
      <AppButton
        label={dialogData.cancelButtonLabel || t('button.cancel')}
        severity="secondary"
        type="outlined"
        isDisabled={isAccepting}
        onClick={() => {
          if (dialogData.onReject) dialogData.onReject();
          resetDialogData();
        }}
      />
    </FlexContainer>
  );

  const warningFooter = (
    <>
      <FlexContainer justify="flex-start">
        {dialogData.showUpload ? (
          dialogData.showUpload(resetDialogData, onDoNotShowAgain)
        ) : (
          <AppButton
            label={dialogData.confirmButtonLabel || t('generic.continue')}
            severity="success"
            state={acceptState}
            isDisabled={isAccepting}
            onClick={() => handleAccept(onDoNotShowAgain)}
          />
        )}
        <AppButton
          label={t('button.cancel')}
          severity="secondary"
          type="outlined"
          className="ml-3"
          isDisabled={isAccepting}
          onClick={() => {
            if (dialogData.onReject) dialogData.onReject();
            resetDialogData();
          }}
        />
      </FlexContainer>

      {dialogData.showDialogConfirmation && (
        <div
          className="flex align-items-center mt-4"
          data-id="dialogConfirmation"
        >
          <Checkbox
            inputId="dialogConfirmation"
            checked={skip}
            name="dialogConfirmation"
            onChange={(e: CheckboxChangeEvent) => {
              setSkip(e.checked ?? false);
            }}
          />
          <label htmlFor="dialogConfirmation" className="ml-2">
            {dialogData.confirmationMessage}
          </label>
        </div>
      )}
    </>
  );

  const footer = () => {
    let result;

    switch (dialogData.type) {
      case DialogType.CONFIRMATION:
        result = dialogData.destructive
          ? destructiveConfirmationFooter
          : confirmationFooter;
        break;
      case DialogType.INFO:
        result = infoFooter;
        break;
      case DialogType.WARNING:
        result = warningFooter;
        break;
    }

    return result;
  };

  const styles = classNames({
    [`dialog-${dialogData.type}`]: dialogData.type,
    ['v2']: dialogData.type === DialogType.WARNING,
  });

  return (
    <DialogContext.Provider value={{ setDialogData }}>
      {children}
      <StyledDialog
        draggable={false}
        visible={dialogData.show}
        header={dialogData.header}
        modal
        closable={!isAccepting}
        closeOnEscape={!isAccepting}
        footer={footer()}
        onHide={resetDialogData}
        type={dialogData.type}
        className={styles}
      >
        <>
          <span>{dialogData.message}</span>
          {dialogData.customContent}
          {dialogData.confirmationMessage &&
            dialogData.type !== DialogType.WARNING && (
              <div
                className="flex align-items-center mt-4"
                data-id="dialogConfirmation"
              >
                <Checkbox
                  inputId="dialogConfirmation"
                  checked={confirmation}
                  name="dialogConfirmation"
                  onChange={(e: CheckboxChangeEvent) =>
                    setConfirmation(e.checked ?? false)
                  }
                />
                <label htmlFor="dialogConfirmation" className="ml-2">
                  {dialogData.confirmationMessage}
                </label>
              </div>
            )}
        </>
      </StyledDialog>
    </DialogContext.Provider>
  );
};
